
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Terminal from 'plaid-threads/Icons/Terminal';
export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Reference for integrating with the Link React Native SDK. Includes requirements, compatibility information, and steps for getting started.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link React Native SDK' subtitle='Reference for integrating with the Link React Native SDK' mdxType="Header" />
    <p>{`This guide covers the latest major version of the Link React Native SDK, which is version 13.x.x. For information on migrating from older versions, see `}<a parentName="p" {...{
        "href": "#migration-guides"
      }}>{`Migration guides`}</a>{`.`}</p>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <Callout icon={<Terminal mdxType="Terminal" />} mdxType="Callout">
      <p>{`Prefer to learn with code examples? A `}<a parentName="p" {...{
          "href": "https://github.com/plaid/tiny-quickstart/tree/main/react_native"
        }}>{`GitHub repo`}</a>{` showing a working example Link implementation is available for this topic.`}</p>
    </Callout>
    <p>{`To get started with `}<a parentName="p" {...{
        "href": "https://github.com/plaid/react-native-plaid-link-sdk"
      }}>{`Plaid Link for React Native`}</a>{` you'll want to sign up
for `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`free API keys`}</a>{` through the Plaid Dashboard.`}</p>
    <h4 {...{
      "id": "requirements"
    }}>{`Requirements`}</h4>
    <ul>
      <li parentName="ul">{`React Native `}<inlineCode parentName="li">{`0.76`}</inlineCode>{` or higher`}</li>
      <li parentName="ul">{`Expo `}<inlineCode parentName="li">{`52`}</inlineCode>{` or higher, or a bare React Native app with `}<a parentName="li" {...{
          "href": "https://docs.expo.dev/bare/installing-expo-modules/"
        }}>{`Expo Modules`}</a>{` installed. Expo Go is not supported, as Plaid Link includes custom native code.`}</li>
    </ul>
    <blockquote>
      <p parentName="blockquote">{`New versions of the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/react-native-plaid-link-sdk"
        }}>{`React Native SDK`}</a>{` are released frequently. Major releases occur annually. The Link SDK uses Semantic Versioning, ensuring that all non-major releases are non-breaking, backwards-compatible updates. We recommend you update regularly (at least once a quarter, and ideally once a month) to ensure the best Plaid Link experience in your application. `}</p>
    </blockquote>
    <p>{`SDK versions are supported for two years; with each major SDK release, Plaid will stop officially supporting any previous SDK versions that are more than two years old. While these older versions are expected to continue to work without disruption, Plaid will not provide assistance with unsupported SDK versions.`}</p>
    <h5 {...{
      "id": "version-compatibility"
    }}>{`Version Compatibility`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`React Native SDK`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Android SDK`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`iOS SDK`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Status`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`13.x.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`6.0.0+`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`>=7.0.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Active, supports Xcode 16.1`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`12.x.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`5.0+`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`>=6.0.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Active, supports Xcode 16`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`11.x.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`4.1.1+`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`>=5.1.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Deprecated, supports Xcode 15`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`10.x.x`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`3.10.1+`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`>=4.1.0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Deprecated, supports Xcode 14`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h4>
    <h5 {...{
      "id": "installing-the-sdk"
    }}>{`Installing the SDK`}</h5>
    <p>{`The SDK is built on `}<a parentName="p" {...{
        "href": "https://docs.expo.dev/bare/installing-expo-modules/"
      }}>{`Expo Modules`}</a>{` and supports Expo apps and bare React Native apps. Because Plaid Link includes native iOS and Android code, it is not supported in Expo Go.`}</p>
    <p>{`In an Expo project, install the package, then rebuild your development build (or other custom native build) so the Plaid native module is included:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Install the SDK",
        "switcher": "false",
        "#Install": true,
        "the": true,
        "SDK": true
      }}>{`npm install react-native-plaid-link-sdk
`}</code></pre>
    <p>{`In a bare React Native project, install and configure Expo Modules first, then install the SDK and its native dependencies:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Install native dependencies",
        "switcher": "false",
        "#Install": true,
        "native": true,
        "dependencies": true
      }}>{`npm install react-native-plaid-link-sdk expo
npx pod-install
`}</code></pre>
    <h5 {...{
      "id": "ios-setup"
    }}>{`iOS Setup`}</h5>
    <p>{`The SDK's iOS native dependencies are installed via CocoaPods through autolinking. After installing the package, run:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Install iOS dependencies",
        "switcher": "false",
        "#Install": true,
        "iOS": true,
        "dependencies": true
      }}>{`npx pod-install
`}</code></pre>
    <p>{`Requirements:`}</p>
    <ul>
      <li parentName="ul">{`iOS 15.1 or higher.`}</li>
      <li parentName="ul">{`Xcode 16.1 or higher.`}</li>
    </ul>
    <h5 {...{
      "id": "android-setup"
    }}>{`Android Setup`}</h5>
    <p>{`Requirements:`}</p>
    <ul>
      <li parentName="ul">{`Android 7.1 (API level 25) and above.`}</li>
      <li parentName="ul">{`Your compileSdkVersion must be 36.`}</li>
      <li parentName="ul">{`Kotlin 1.9.25 or higher.`}</li>
    </ul>
    <p>{`Autolinking should handle all of the Android setup.`}</p>
    <ul>
      <li parentName="ul">{`Register your Android package name in the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}>{`Dashboard`}</a>{`. This is required in order to connect to OAuth institutions (which includes most major banks).`}</li>
    </ul>
    <h6 {...{
      "id": "sample-app"
    }}>{`Sample app`}</h6>
    <p>{`For a sample app that demonstrates a minimal integration with the React Native Plaid Link SDK, see the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/tiny-quickstart/tree/main/react_native"
      }}>{`Tiny Quickstart (React Native)`}</a>{`.`}</p>
    <h4 {...{
      "id": "opening-link"
    }}>{`Opening Link`}</h4>
    <p>{`Before you can open Link, you need to first create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` can be configured for
different Link flows and is used to control much of Link's behavior. To see how to create a new
`}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, see the API Reference entry for `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.
If your React Native application will be used on Android, the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call should include the `}<inlineCode parentName="p">{`android_package_name`}</inlineCode>{` parameter.
Each time you open Link, you will need to get a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` from your server.`}</p>
    <p>{`Create a Link session by calling `}<inlineCode parentName="p">{`createPlaidLinkSession`}</inlineCode>{` with your `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` and callbacks, then open it with `}<inlineCode parentName="p">{`session.open()`}</inlineCode>{`. Maximizing the delay between creating the session and opening it reduces user-facing latency by giving Link more time to load.`}</p>
    <h4 {...{
      "id": "createplaidlinksession"
    }}><inlineCode parentName="h4">{`createPlaidLinkSession()`}</inlineCode></h4>
    <Schema type='link' sdk='react_native' method='create' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #create() example",
          "switcher": "false",
          "#create()": true,
          "example": true
        }}>{`const session = await createPlaidLinkSession({
  token: linkToken,
  onSuccess: (success: LinkSuccess) => {
    console.log(success);
  },
  onExit: (exit: LinkExit) => {
    console.log(exit);
  },
  onEvent: (event: LinkEvent) => {
    console.log(event);
  },
});
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "open"
    }}><inlineCode parentName="h4">{`open()`}</inlineCode></h4>
    <Schema type='link' sdk='react_native' method='open' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-tsx",
          "metastring": "switcher=false #open() example",
          "switcher": "false",
          "#open()": true,
          "example": true
        }}>{`<TouchableOpacity
  style={styles.button}
  onPress={() => session.open()}>
  <Text style={styles.button}>Open Link</Text>
</TouchableOpacity>
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "onsuccess"
    }}><inlineCode parentName="h4">{`onSuccess`}</inlineCode></h4>
    <Schema type='link' sdk='react_native' method='onSuccess' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #onSuccess example",
          "switcher": "false",
          "#onSuccess": true,
          "example": true
        }}>{`const onSuccess = (success: LinkSuccess) => {
  // If using Item-based products, exchange public_token
  // for access_token
  fetch('https://yourserver.com/exchange_public_token', {
    method: 'POST',
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      publicToken: success.publicToken,
      accounts: success.metadata.accounts,
      institution: success.metadata.institution,
      linkSessionId: success.metadata.linkSessionId,
    }),
  });
};
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "onexit"
    }}><inlineCode parentName="h4">{`onExit`}</inlineCode></h4>
    <Schema type='link' sdk='react_native' method="onExit" mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #onExit example",
          "switcher": "false",
          "#onExit": true,
          "example": true
        }}>{`const onExit = (linkExit: LinkExit) => {
  supportHandler.report({
    error: linkExit.error,
    institution: linkExit.metadata.institution,
    linkSessionId: linkExit.metadata.linkSessionId,
    requestId: linkExit.metadata.requestId,
    status: linkExit.metadata.status,
  });
};
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "onevent"
    }}><inlineCode parentName="h4">{`onEvent`}</inlineCode></h4>
    <p>{`The React Native Plaid module emits `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` events throughout the account linking process.
To receive these events, pass an `}<inlineCode parentName="p">{`onEvent`}</inlineCode>{` callback to `}<inlineCode parentName="p">{`createPlaidLinkSession`}</inlineCode>{` (or to `}<inlineCode parentName="p">{`createPlaidLayerSession`}</inlineCode>{` or `}<inlineCode parentName="p">{`createPlaidHeadlessSession`}</inlineCode>{`).`}</p>
    <Schema type='link' sdk='react_native' method='onEvent' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #onEvent example",
          "switcher": "false",
          "#onEvent": true,
          "example": true
        }}>{`const onEvent = (event: LinkEvent) => {
  console.log(event);
};
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "submit"
    }}><inlineCode parentName="h4">{`submit()`}</inlineCode></h4>
    <Schema type='link' sdk='react_native' method='submit' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #submit() example",
          "switcher": "false",
          "#submit()": true,
          "example": true
        }}>{`await layerSession.submit({
  phoneNumber: '+14155550123',
});
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "oauth"
    }}>{`OAuth`}</h4>
    <p>{`Using Plaid Link with an OAuth flow requires some additional setup instructions. For details, see the `}<a parentName="p" {...{
        "href": "/docs/link/oauth"
      }}>{`OAuth guide`}</a>{`.`}</p>
    <h4 {...{
      "id": "upgrading"
    }}>{`Upgrading`}</h4>
    <p>{`The latest version of the SDK is available from `}<a parentName="p" {...{
        "href": "https://github.com/plaid/react-native-plaid-link-sdk"
      }}>{`GitHub`}</a>{`. New versions of the SDK are released frequently. Major releases occur annually. The Link SDK uses Semantic Versioning, ensuring that all non-major releases are non-breaking, backwards-compatible updates. We recommend you update regularly (at least once a quarter, and ideally once a month) to ensure the best Plaid Link experience in your application. `}</p>
    <p>{`SDK versions are supported for two years; with each major SDK release, Plaid will stop officially supporting any previous SDK versions that are more than two years old. While these older versions are expected to continue to work without disruption, Plaid will not provide assistance with unsupported SDK versions. `}</p>
    <h5 {...{
      "id": "migration-guides"
    }}>{`Migration guides`}</h5>
    <ul>
      <li parentName="ul">{`Version 13.x rebuilds the SDK on `}<a parentName="li" {...{
          "href": "https://docs.expo.dev/bare/installing-expo-modules/"
        }}>{`Expo Modules`}</a>{` and replaces the global `}<inlineCode parentName="li">{`create`}</inlineCode>{`, `}<inlineCode parentName="li">{`open`}</inlineCode>{`, `}<inlineCode parentName="li">{`submit`}</inlineCode>{`, `}<inlineCode parentName="li">{`openLink`}</inlineCode>{`, `}<inlineCode parentName="li">{`PlaidLink`}</inlineCode>{`, and `}<inlineCode parentName="li">{`usePlaidEmitter`}</inlineCode>{` APIs with session objects created via `}<inlineCode parentName="li">{`createPlaidLinkSession`}</inlineCode>{`, `}<inlineCode parentName="li">{`createPlaidLayerSession`}</inlineCode>{`, and `}<inlineCode parentName="li">{`createPlaidHeadlessSession`}</inlineCode>{`. It also removes `}<inlineCode parentName="li">{`destroy`}</inlineCode>{`, renames the `}<inlineCode parentName="li">{`EmbeddedLinkView`}</inlineCode>{` component to `}<inlineCode parentName="li">{`PlaidEmbeddedSearchView`}</inlineCode>{`, replaces `}<inlineCode parentName="li">{`LinkError.errorDisplayMessage`}</inlineCode>{` with `}<inlineCode parentName="li">{`displayMessage`}</inlineCode>{`, and requires React Native 0.76+, iOS 15.1+ with Xcode 16.1+, and Android SDK 6.0 (minSdk 25, compileSdk 36). For details, see the `}<a parentName="li" {...{
          "href": "https://github.com/plaid/react-native-plaid-link-sdk/blob/master/V13_MIGRATION_GUIDE.md"
        }}>{`v13 migration guide`}</a>{`.`}</li>
      <li parentName="ul">{`Version 12.x removes the `}<inlineCode parentName="li">{`PlaidLink`}</inlineCode>{` component and `}<inlineCode parentName="li">{`openLink`}</inlineCode>{` function, which were deprecated in version 11.6.0. If you are using this method of opening Link, replace it with the new process that uses `}<inlineCode parentName="li">{`create`}</inlineCode>{` and then `}<inlineCode parentName="li">{`open`}</inlineCode>{`. For details, see `}<a parentName="li" {...{
          "href": "#opening-link"
        }}>{`Opening Link`}</a>{` or the `}<a parentName="li" {...{
          "href": "https://github.com/plaid/react-native-plaid-link-sdk/blob/v12.8.5/README.md"
        }}>{`README`}</a>{`. Version 12.x also removes the `}<inlineCode parentName="li">{`PROFILE_ELIGIBILITY_CHECK_ERROR`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Version 12.x updates the Android target SDK version and compile version from 33 to 35 and requires use of an Xcode 16 toolchain. `}</li>
      <li parentName="ul">{`Version 11.x contains several breaking changes from previous major versions. For details, see the `}<a parentName="li" {...{
          "href": "https://github.com/plaid/react-native-plaid-link-sdk/blob/v11.0.0/v11-migration-guide.md"
        }}>{`migration guide on GitHub`}</a>{`.`}</li>
      <li parentName="ul">{`When upgrading from 9.x to 10.x or later, you should remove any invocation of `}<inlineCode parentName="li">{`useDeepLinkRedirector`}</inlineCode>{` on iOS, as it has been removed from the SDK, since it is no longer required for handling Universal Links. You must make sure you are using a compatible version of React Native (0.66.0 or higher) and the Plaid iOS SDK (see the `}<a parentName="li" {...{
          "href": "https://github.com/plaid/react-native-plaid-link-sdk#compatibility"
        }}>{`version compatibility chart`}</a>{` on GitHub).`}</li>
      <li parentName="ul">{`No code changes are required to upgrade from 8.x to 9.x, although you must make sure you are using a compatible version of the Plaid iOS SDK (4.1.0 or higher).`}</li>
      <li parentName="ul">{`The only difference between version 7.x and 8.x is that 8.x adds support for Xcode 14. No code changes are required to upgrade from 7.x to 8.x, although you must convert to an Xcode 14 toolchain.`}</li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "requirements",
        level: 4,
        title: "Requirements",
      },
{
        id: "getting-started",
        level: 4,
        title: "Getting Started",
      },
{
        id: "opening-link",
        level: 4,
        title: "Opening Link",
      },
{
        id: "createplaidlinksession",
        level: 4,
        title: "createPlaidLinkSession()",
      },
{
        id: "open",
        level: 4,
        title: "open()",
      },
{
        id: "onsuccess",
        level: 4,
        title: "onSuccess",
      },
{
        id: "onexit",
        level: 4,
        title: "onExit",
      },
{
        id: "onevent",
        level: 4,
        title: "onEvent",
      },
{
        id: "submit",
        level: 4,
        title: "submit()",
      },
{
        id: "oauth",
        level: 4,
        title: "OAuth",
      },
{
        id: "upgrading",
        level: 4,
        title: "Upgrading",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
